// Advancing the world through time, driven by whoever is calling.
//
// Until now (2026-07-30, sim extraction step 5) the two ambient rules ran on
// Phaser timers registered inside GameScene: `this.time.addEvent` at 20s for the
// player's passive Mana trickle and at 3s for environmental decay. That made
// elapsed time something only a running browser could produce, which is why
// questions like "can the player simply wait out this puzzle?" could only be
// answered by sitting in front of the real game -- or by a Playwright assertion
// racing a wall-clock timer, which is exactly why room5.spec.ts's exact
// energy-bar check is unreliable on a slower machine.
//
// Time is now an argument. `tick(world, dtMs)` accumulates and fires each rule at
// its own interval; the game passes Phaser's per-frame delta, and a test passes
// however many milliseconds it wants to skip. Nothing here reads a clock.
import { WorldState } from './state';
import { SimEvents } from './events';
import { isTurnClock } from './physics';
import { applyRoomDecay } from './systems';
import { solveThermalStep } from './thermal';
import { relaxChargeNetworks } from './chargeBind';
import { settleWorld } from './settle';
import { advanceMobs } from './mob';
import { advanceConveyors } from './conveyor';

/** How many of each ambient rule actually fired during a tick. The renderer uses
 * this to decide whether the HUD and debug readouts need refreshing, which keeps
 * those refreshes exactly as frequent as the old per-timer callbacks were rather
 * than running them every frame. */
export interface TickSummary {
  regenTicks: number;
  decayTicks: number;
  /** How many tiles the room's conveyors carried something across. Its own
   * count, beside the mob ones and for the same reason: a belt runs on its own
   * cadence, which none of the other intervals need divide, so a renderer that
   * refreshed only on a decay tick would leave a boulder drawn on the tile the
   * world says it has left. */
  conveyorSteps: number;
  /** How many mob steps and mob casts happened -- counted rather than flagged
   * for the same reason the other two are, and reported separately because mobs
   * run on their own cadences: a renderer that refreshed only when a decay tick
   * fired would miss both entirely, since none of these intervals divides any
   * other. Two counts rather than one because a mob that stood still and threw a
   * spell moved nothing, and the renderer still has to draw the spell. */
  mobSteps: number;
  mobCasts: number;
}

/** Advances the world by `dtMs`.
 *
 * Both rules loop rather than firing at most once, so a single large step behaves
 * the same as many small ones -- `tick(world, 80_000)` applies four 20-second
 * regen ticks, which is what makes "advance a minute and see whether this puzzle
 * became solvable" a real question a test can ask. The leftover remainder is
 * carried, so repeated ragged steps don't drift.
 *
 * Deliberately does NOT gate itself on anything: whether the world should be
 * ticking at all (the game holds off until the title/intro screens close) is the
 * caller's business, not the simulation's.
 */
export function tick(world: WorldState, dtMs: number, out: SimEvents): TickSummary {
  const summary: TickSummary = { regenTicks: 0, decayTicks: 0, conveyorSteps: 0, mobSteps: 0, mobCasts: 0 };
  if (dtMs <= 0) return summary;

  const { player: playerPhysics, room: roomPhysics } = world.physics;
  world.timeMs += dtMs;

  // An interval of 0 or less would make every loop below subtract nothing
  // forever: an unbreakable hang, with no error and no frame, in a tab that may
  // be holding hours of unsaved authoring. Reachable because the editor's physics
  // panel surfaces every numeric leaf as an editable dial and `tunePhysics` writes
  // whatever it is given (`review/2026-08-07-architecture-review.md` §4.7), and
  // considerably more reachable since 2026-08-12, when `decayTickMs` became a
  // number people are actively expected to tune. Skipped rather than clamped:
  // "this rule does not fire" is a coherent thing for an interval of 0 to mean,
  // and it is what `advanceMobs` already does with `stepMs`/`castMs`.
  const regenTickMs = playerPhysics.manaRegenTickMs;
  const decayTickMs = roomPhysics.decayTickMs;

  // Ambient metabolism: a passive Mana trickle, independent of any cast.
  world.sinceManaRegenMs += dtMs;
  while (regenTickMs > 0 && world.sinceManaRegenMs >= regenTickMs) {
    world.sinceManaRegenMs -= regenTickMs;
    world.player.mana = Math.min(playerPhysics.maxMana, world.player.mana + playerPhysics.manaRegenPerTick);
    summary.regenTicks++;
  }

  // Environmental Energy decay, toward whatever room the player is currently
  // standing in -- see applyRoomDecay. Other rooms don't tick while unvisited,
  // same as room-object mutations only ever exist for the current room.
  //
  // Deliberately does NOT settle after ordinary decay, unlike every mutation
  // in casts.ts. Decay's reach is narrow enough to argue about: it moves Energy
  // on objects that carry `meltsWhen` (re-checking that threshold as it goes)
  // and refills reservoirs toward `maxProperties`, and no object in any room
  // today is both decayable and read by a system -- room9's freezable riverbed
  // tile carries neither field, and no conduit's standing pillar has
  // `maxProperties`. `sim.test.ts` pins both, so if that stops being true it
  // fails there rather than showing up as water that thaws a few seconds late.
  //
  // Bind·Charge used to be an exception here -- while it conducted on this
  // cadence it moved `properties.charge`, which `conduit-gates` genuinely does
  // read, so a tick that moved Charge had to settle afterward. It no longer
  // conducts on a tick at all: a wire equalizes totally, on settle, in the same
  // pass as whatever mutation charged it (see chargeBind.ts). Nothing about the
  // argument above needs an exception any more.
  //
  // solveThermalStep runs the player's own thermal edges (ambient, contact,
  // any Bind link) here too, on the same cadence -- see thermal.ts's own
  // module comment for why Bind moved off settle and onto this tick instead.
  // applyRoomDecay itself skips every object solveThermalStep already
  // covers (either side of a live Bind link), so the two never double-apply
  // to the same object in the same tick.
  world.sinceRoomDecayMs += dtMs;
  while (decayTickMs > 0 && world.sinceRoomDecayMs >= decayTickMs) {
    world.sinceRoomDecayMs -= decayTickMs;
    const room = world.rooms[world.player.roomId];
    if (room) {
      applyRoomDecay(room, out, world.physics, world.flowingTiles);
      solveThermalStep(world, room, out);
      // The one decay rule that genuinely invalidates a system, and therefore
      // the one exception to the "tick does not settle" note above: a charge
      // network relaxing toward its sources' equilibrium moves exactly the
      // Charge `conduit-gates` reads. Settling here is also what closes the
      // gap where a regenerating source drifted a wired network out of
      // equilibrium between mutations -- reported from play, 2026-08-08.
      // Settled only when Charge actually moved, so the "tick does not settle"
      // rule above still holds for every other decay -- this is one narrow
      // exception, not its removal.
      if (relaxChargeNetworks(world, out)) settleWorld(world, out);
    }
    summary.decayTicks++;
  }

  // Conveyors carry on their own clock, kept here rather than per belt (see
  // conveyor.ts on why machinery is the opposite case from a mob): every run in
  // the room is driven off the same power and steps in the same instant.
  //
  // Before the mobs, deliberately. Both are movers on their own cadence and one
  // of them has to go first; a belt that has already delivered its load is the
  // world a walking creature should be aiming at, rather than the other way
  // round -- a mob that stepped toward where a boulder was about to stop being
  // is chasing a tile that is empty by the time it arrives.
  //
  // The same exception to this file's "a tick does not settle" rule the mobs
  // are, and an equally uninteresting one: a carried object goes through the
  // same `moveObjectTo` a push does, and the caster through the same
  // `movePlayerTo` a step does, which settle because a moved thing can press a
  // pad or dam a channel. That is a mutation site behaving like every other
  // mutation site.
  summary.conveyorSteps = advanceConveyors(world, dtMs, out);

  // Mobs walk on their own clock, kept per mob rather than here (see mob.ts):
  // two mobs on different cadences, or one that arrived mid-room, must not be
  // forced into one shared phase the way the two ambient rules above safely can
  // be. `advanceMobs` does its own accumulating and its own looping, so it is
  // handed the raw delta rather than a fired-this-many-times count.
  //
  // The exception to this file's "a tick does not settle" rule, and an
  // uninteresting one: a mob step goes through the same `moveObjectTo` a push
  // does, which settles because a moved object can satisfy a pad or dam a
  // channel. That is a mutation site behaving like every other mutation site,
  // not a special case for mobs -- see sim/settle.ts's own header.
  const mobs = advanceMobs(world, dtMs, out);
  summary.mobSteps = mobs.steps;
  summary.mobCasts = mobs.casts;

  return summary;
}

/** Advances the world by exactly one player action's worth of time, and reports
 * whether it did anything.
 *
 * Returns false, having changed nothing, on a world running the wall clock
 * (`physics.clock.msPerTurn` of 0) -- so a caller does not have to know which
 * clock it is on, and every call site added for the turn clock is inert in the
 * game as it shipped.
 *
 * **This is the whole of the turn clock.** There is no turn counter, no separate
 * update path, and no second set of intervals: a turn is a `tick` of a known
 * size, so `manaRegenTickMs`, `decayTickMs` and every mob cadence keep working
 * exactly as they always have, just measured against time the player spent
 * rather than time that passed. The mechanics that make that timing *countable*
 * -- decay and charge relaxation moving a fixed number of units per tick and
 * stopping on arrival -- are a separate change, landed the same day; see
 * `physics.ts`'s `countedDelta` and `turnsPer`.
 *
 * Settles, unlike `tick` itself. `tick`'s own header explains why it does not:
 * whether ambient decay can invalidate a system is the caller's business, and it
 * can argue about its reach because it knows the shipped rooms. A turn has no
 * such argument available -- it happens in whatever room the player is in, in the
 * editor and in a playtest of an unshipped room too -- so it assumes it can. */
export function advanceTurn(world: WorldState, out: SimEvents): boolean {
  if (!isTurnClock(world.physics)) return false;
  tick(world, world.physics.clock.msPerTurn, out);
  settleWorld(world, out);
  return true;
}
